import React, { useState, useEffect, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend,
} from 'recharts';
import { alertStore, UnifiedAlert, AlertStage } from '../utils/alertStore';
import { motorHealthEngine } from '../utils/ewmaEngine';
import { CorrelationResult } from '../utils/correlationEngine';
import './Alerts.css';

const SEVERITY_COLOR = { Critical: '#f87171', High: '#f97316', Medium: '#fbbf24', Low: '#60a5fa' };
const SEVERITY_ICON  = { Critical: '🔴', High: '🟠', Medium: '🟡', Low: '🔵' };
const SEVERITY_BG    = {
  Critical: 'linear-gradient(135deg, #7f1d1d 0%, #1a0a0a 100%)',
  High:     'linear-gradient(135deg, #7c2d12 0%, #1a0d06 100%)',
  Medium:   'linear-gradient(135deg, #78350f 0%, #1a1206 100%)',
  Low:      'linear-gradient(135deg, #1e3a5f 0%, #060d1a 100%)',
};
const STAGE_LABEL: Record<AlertStage, string> = {
  EARLY: '🟡 EARLY — Wear Detected',
  MID:   '🟠 MID — Active Degradation',
  LATE:  '🔴 LATE — Imminent Failure',
};
const SEVERITY_TITLE = {
  Critical: '🚨 CRITICAL ALERT',
  High:     '⚠️ HIGH ALERT',
  Medium:   '🟡 MEDIUM ALERT',
  Low:      '🔵 LOW ALERT',
};

type FilterType = 'ALL' | 'Critical' | 'High' | 'Medium' | 'Low';

function Row({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div className="alert-popup-row">
      <span className="alert-popup-label">{label}</span>
      <span className="alert-popup-val" style={color ? { color, fontWeight: 700 } : {}}>{value}</span>
    </div>
  );
}

function EWMAChart({ machineId, sensorId, sensorName, color }: {
  machineId: string; sensorId: string; sensorName: string; color: string;
}) {
  const data = motorHealthEngine.getChartData(machineId, sensorId);
  if (data.length < 3) return (
    <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 12, padding: '16px 0' }}>
      Collecting data points...
    </div>
  );
  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="t" stroke="#4b5563" tick={{ fontSize: 9 }} label={{ value: 'cycles', position: 'insideBottomRight', offset: 0, fontSize: 9, fill: '#4b5563' }} />
        <YAxis stroke="#4b5563" tick={{ fontSize: 9 }} />
        <Tooltip
          contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 6, fontSize: 11 }}
          formatter={(v: any) => [+v.toFixed(3)]}
        />
        <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
        <ReferenceLine y={data[0]?.ucl3} stroke="#f87171" strokeDasharray="4 3" label={{ value: 'UCL 3σ', fill: '#f87171', fontSize: 9 }} />
        <ReferenceLine y={data[0]?.ucl2} stroke="#fbbf24" strokeDasharray="4 3" label={{ value: 'UCL 2σ', fill: '#fbbf24', fontSize: 9 }} />
        <Line type="monotone" dataKey="raw"  stroke="#6b7280" strokeWidth={1} dot={false} name="Raw" />
        <Line type="monotone" dataKey="ewma" stroke={color}   strokeWidth={2} dot={false} name="EWMA" />
      </LineChart>
    </ResponsiveContainer>
  );
}

function AlertPopup({ alert, onClose, onAck }: { alert: UnifiedAlert; onClose: () => void; onAck: () => void }) {
  const color = SEVERITY_COLOR[alert.severity];
  const ctDelta = alert.cycletime && alert.ctBaseline
    ? (((alert.cycletime - alert.ctBaseline) / alert.ctBaseline) * 100).toFixed(1)
    : null;

  return (
    <div className="alert-popup-overlay" onClick={onClose}>
      <div className="alert-popup" style={{ background: SEVERITY_BG[alert.severity], borderColor: color }} onClick={e => e.stopPropagation()}>

        <div className="alert-popup-header" style={{ borderBottomColor: `${color}55` }}>
          <div>
            <div className="alert-popup-title" style={{ color }}>
              {SEVERITY_TITLE[alert.severity]}
            </div>
            <div className="alert-popup-sub">
              {alert.stage ? STAGE_LABEL[alert.stage] : alert.recommendedAction}
            </div>
          </div>
          <button className="alert-popup-close" onClick={onClose}>✕</button>
        </div>

        <div className="alert-popup-body">
          {/* Source + severity badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div className="alert-popup-badge" style={{ background: `${color}22`, border: `1px solid ${color}`, color }}>
              {alert.severity}
            </div>
            <div className="alert-popup-badge" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#9ca3af' }}>
              {alert.source === 'ewma' ? '📡 Motor Health' : '🧪 What-If'}
            </div>
          </div>

          <Row label="Machine" value={alert.machineName} />
          <Row label="Dominant Sensor" value={alert.sensorName} />

          {/* Fused health score + confidence */}
          {alert.healthScore !== undefined && (
            <div style={{ display: 'flex', gap: 12, margin: '4px 0' }}>
              <div style={{ flex: 1, padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Machine Health</div>
                <div style={{ fontSize: 26, fontWeight: 800,
                  color: alert.healthScore > 70 ? '#34d399' : alert.healthScore > 40 ? '#fbbf24' : '#f87171' }}>
                  {alert.healthScore}%
                </div>
              </div>
              <div style={{ flex: 1, padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Confidence</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#a78bfa' }}>{alert.confidence}%</div>
              </div>
            </div>
          )}

          {/* Confidence breakdown bars */}
          {alert.confidenceBreakdown && (
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>🎯 Confidence Breakdown</div>
              {([
                { label: 'Data Maturity',      value: alert.confidenceBreakdown.dataMaturity,      tip: 'Cycles collected vs ideal' },
                { label: 'Slope Consistency',  value: alert.confidenceBreakdown.slopeConsistency,  tip: 'Trend steadiness across sensors' },
                { label: 'Sensor Agreement',   value: alert.confidenceBreakdown.sensorAgreement,   tip: 'Sensors agreeing on stage' },
                { label: 'Signal Strength',    value: alert.confidenceBreakdown.signalStrength,    tip: 'How far above UCL threshold' },
                { label: 'Stage Stability',    value: alert.confidenceBreakdown.stageStability,    tip: 'Consecutive cycles in this stage' },
              ] as { label: string; value: number; tip: string }[]).map(f => (
                <div key={f.label} style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af' }} title={f.tip}>{f.label}</span>
                    <span style={{ fontSize: 11, color: '#a78bfa', fontWeight: 600 }}>{f.value}%</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)' }}>
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${f.value}%`,
                      background: f.value > 70 ? '#34d399' : f.value > 40 ? '#a78bfa' : '#fbbf24',
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {alert.source === 'ewma' && <>
            <Row label="Avg Slope (all sensors)"
              value={`${(alert.avgSlope ?? 0) > 0 ? '+' : ''}${alert.avgSlope?.toFixed(4)}/cycle`}
              color={(alert.avgSlope ?? 0) > 0.01 ? '#f87171' : '#fbbf24'} />
            {alert.ttf && alert.ttf > 0 && (
              <Row label="Time to Failure" value={`~${alert.ttf} cycles`}
                color={alert.ttf < 50 ? '#f87171' : '#fbbf24'} />
            )}

            {/* Baseline shift warning */}
            {alert.baselineShiftSensors && alert.baselineShiftSensors.length > 0 && (
              <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #f97316aa' }}>
                <div style={{ padding: '8px 12px', background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>📈</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#f97316' }}>Operating Point Shifted</span>
                </div>
                <div style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.2)', fontSize: 12 }}>
                  <div style={{ color: '#9ca3af', marginBottom: 8 }}>
                    Baseline automatically re-learned. New “normal” is significantly different from original.
                  </div>
                  {alert.baselineShiftSensors.map(s => (
                    <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between',
                      padding: '5px 8px', marginBottom: 4, borderRadius: 6,
                      background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}>
                      <span style={{ color: '#e5e7eb', fontWeight: 600 }}>{s.name}</span>
                      <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                        <span style={{ color: '#9ca3af' }}>Was: {s.prevMu}</span>
                        <span style={{ color: '#f97316' }}>→ Now: {s.newMu}</span>
                        <span style={{ color: '#fbbf24' }}>Δ{s.delta}σ</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detected failure patterns */}
            {alert.detectedPatterns && alert.detectedPatterns.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>🔍 Detected Failure Patterns</div>
                {alert.detectedPatterns.map((p, i) => (
                  <div key={p.id} style={{
                    marginBottom: 10, borderRadius: 8, overflow: 'hidden',
                    border: `1px solid ${i === 0 ? color + '66' : 'rgba(255,255,255,0.08)'}`,
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 12px',
                      background: i === 0 ? `${color}18` : 'rgba(255,255,255,0.04)',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? color : '#e5e7eb' }}>
                        {p.icon} {p.name}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: p.matchScore > 75 ? '#f8717133' : p.matchScore > 50 ? '#fbbf2433' : '#6b728033',
                        color:      p.matchScore > 75 ? '#f87171'   : p.matchScore > 50 ? '#fbbf24'   : '#9ca3af',
                      }}>
                        {p.matchScore}% match
                      </span>
                    </div>
                    <div style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.2)', fontSize: 12 }}>
                      <div style={{ color: '#9ca3af', marginBottom: 4 }}>{p.description}</div>
                      <div style={{ color: '#fbbf24', marginBottom: 4 }}>⚠️ Root cause: {p.rootCause}</div>
                      <div style={{ color: '#34d399' }}>🔧 Fix: {p.fix}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Per-sensor breakdown + live charts */}
            {alert.sensorDetails && alert.sensorDetails.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>📊 Per-Sensor Breakdown</div>
                {alert.sensorDetails.map(s => {
                  const sc = s.stage === 'LATE' ? '#f87171' : s.stage === 'MID' ? '#f97316' : s.stage === 'EARLY' ? '#fbbf24' : '#34d399';
                  return (
                    <div key={s.name} style={{ marginBottom: 12, borderRadius: 8, border: `1px solid ${sc}33`, overflow: 'hidden' }}>
                      {/* Sensor header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 10px', background: 'rgba(255,255,255,0.04)' }}>
                        <span style={{ fontSize: 12, color: '#e5e7eb', fontWeight: 600 }}>{s.name}</span>
                        <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                          <span style={{ color: '#9ca3af' }}>EWMA: {s.ewma.toFixed(2)}</span>
                          <span style={{ color: s.slope > 0.005 ? '#f87171' : '#6b7280' }}>
                            {s.slope > 0 ? '+' : ''}{s.slope.toFixed(4)}/c
                          </span>
                          <span style={{ color: sc, fontWeight: 700 }}>{s.stage}</span>
                        </div>
                      </div>
                      {/* Live EWMA chart for this sensor */}
                      <div style={{ padding: '8px 4px 4px', background: 'rgba(0,0,0,0.2)' }}>
                        <EWMAChart
                          machineId={s.machineId}
                          sensorId={s.sensorId}
                          sensorName={s.name}
                          color={sc}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>}

          {alert.source === ('slider' as any) && (
            <div style={{ marginTop: 4 }}>
              {alert.recommendedAction.includes('Recovering') ? (
                // Recovery popup
                <div style={{ padding: '14px 16px', borderRadius: 10,
                  background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)' }}>
                  <div style={{ fontSize: 16, marginBottom: 8 }}>🔄 Recovery Detected</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#34d399', marginBottom: 8 }}>
                    {alert.recommendedAction}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
                    <strong style={{ color: '#e5e7eb' }}>{alert.sensorName}</strong> EWMA dropped to{' '}
                    <strong style={{ color: '#34d399' }}>{alert.currentValue}{alert.unit}</strong>
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', padding: '8px 10px', borderRadius: 6,
                    background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}>
                    💡 Keep monitoring — drag slider back to 0 to fully recover to Normal
                  </div>
                </div>
              ) : (
                // Normal threshold alert popup
                <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8,
                  background: `${color}11`, border: `1px solid ${color}33`, fontSize: 12, color: '#9ca3af' }}>
                  <strong style={{ color }}>{alert.sensorName}</strong> value of{' '}
                  <strong style={{ color }}>{alert.currentValue}{alert.unit}</strong>{' '}
                  crossed the <strong style={{ color }}>{alert.severity}</strong> threshold.
                </div>
              )}
            </div>
          )}

          {alert.source === 'whatif' && <>
            <Row label="Failure Probability" value={`${alert.failureProb}%`} color={color} />
            <Row label="Remaining Useful Life" value={`${alert.rul} days`}
              color={alert.rul <= 14 ? '#f87171' : '#34d399'} />
            {/* EWMA charts for simulation sensors */}
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>📊 Simulated Sensor Trends (EWMA)</div>
              {[
                { label: 'Temperature', key: 'temp',  unit: '°C', color: '#f87171' },
                { label: 'Vibration',   key: 'vib',   unit: 'g',   color: '#f97316' },
                { label: 'Speed',       key: 'speed', unit: 'RPM', color: '#fbbf24' },
              ].map(s => (
                <div key={s.key} style={{ marginBottom: 12, borderRadius: 8,
                  border: `1px solid ${s.color}33`, overflow: 'hidden' }}>
                  <div style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)',
                    fontSize: 12, color: '#e5e7eb', fontWeight: 600 }}>
                    {s.label} ({s.unit})
                  </div>
                  <div style={{ padding: '8px 4px 4px', background: 'rgba(0,0,0,0.2)' }}>
                    <EWMAChart machineId="whatif" sensorId={s.key} sensorName={s.label} color={s.color} />
                  </div>
                </div>
              ))}
            </div>
          </>}

          <Row label="Timestamp" value={alert.timestamp} />

          <div className="alert-popup-action" style={{ borderColor: `${color}55`, background: `${color}11` }}>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>💡 Recommended Action</div>
            <div style={{ color: '#e5e7eb', fontSize: 14 }}>{alert.recommendedAction}</div>
          </div>
        </div>

        <button className="alert-popup-ack" style={{ background: color }} onClick={onAck}>Acknowledge</button>
      </div>
    </div>
  );
}

const Alerts: React.FC = () => {
  const [alerts, setAlerts]             = useState<UnifiedAlert[]>([]);
  const [filter, setFilter]             = useState<FilterType>('ALL');
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());
  const [popup, setPopup]               = useState<UnifiedAlert | null>(null);
  const [prevTopId, setPrevTopId]       = useState<string | null>(null);
  const [correlation, setCorrelation]   = useState<CorrelationResult | null>(null);
  const [alertEmails, setAlertEmails]   = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('alertPreferences') || '{}').notificationEmails || []; }
    catch { return []; }
  });
  const [newEmail, setNewEmail]         = useState('');
  const [emailSaved, setEmailSaved]     = useState(false);
  const shownRecoveryIds                = useRef<Set<string>>(new Set());

  const saveEmails = (emails: string[]) => {
    try {
      const prefs = JSON.parse(localStorage.getItem('alertPreferences') || '{}');
      prefs.notificationEmails = emails;
      prefs.emailOnCritical = true;
      prefs.emailOnWarning  = true;
      localStorage.setItem('alertPreferences', JSON.stringify(prefs));
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 2000);
    } catch {}
  };

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e || alertEmails.includes(e)) return;
    const updated = [...alertEmails, e];
    setAlertEmails(updated);
    saveEmails(updated);
    setNewEmail('');
  };

  const removeEmail = (email: string) => {
    const updated = alertEmails.filter(e => e !== email);
    setAlertEmails(updated);
    saveEmails(updated);
  };

  useEffect(() => {
    const update = () => {
      const all = alertStore.getAlerts().filter(a => a.source === 'whatif' || a.source === ('slider' as any));
      setAlerts(all);
      setCorrelation(null);
      setAcknowledged(ackedSet => {
        if (all.length > 0) {
          // Open recovery alert only once (never re-show after seen or acknowledged)
          const latestRecovery = all.find(a =>
            a.recommendedAction?.includes('Recovering') &&
            !ackedSet.has(a.id) &&
            !shownRecoveryIds.current.has(a.id)
          );
          if (latestRecovery) {
            shownRecoveryIds.current.add(latestRecovery.id);
            setPrevTopId(latestRecovery.id);
            setPopup(latestRecovery);
            return ackedSet;
          }
          // Open new non-recovery alert only if no popup is open and not acknowledged
          const top = all.find(a => !ackedSet.has(a.id));
          if (top && top.id !== prevTopId) {
            setPrevTopId(top.id);
            setPopup(prev => prev === null ? top : prev);
          }
        } else {
          setPrevTopId(null);
          setPopup(null);
        }
        return ackedSet;
      });
    };
    update();
    const unsub = alertStore.subscribe(update);
    return () => { unsub(); };
  }, [prevTopId]);

  const visible = alerts.filter(a =>
    (filter === 'ALL' || a.severity === filter) && !acknowledged.has(a.id)
  );
  const counts = {
    Critical: alerts.filter(a => a.severity === 'Critical').length,
    High:     alerts.filter(a => a.severity === 'High').length,
    Medium:   alerts.filter(a => a.severity === 'Medium').length,
    Low:      alerts.filter(a => a.severity === 'Low').length,
  };
  const ewmaCount   = alerts.filter(a => a.source === 'ewma').length;
  const whatifCount = alerts.filter(a => a.source === 'whatif').length;

  const closePopup = () => setPopup(null);
  const ackPopup   = (id: string) => { setPopup(null); setPrevTopId(id); setAcknowledged(prev => new Set([...prev, id])); };
  const ack        = (id: string) => setAcknowledged(prev => new Set([...prev, id]));
  const ackAll     = () => setAcknowledged(new Set(alerts.map(a => a.id)));
  const clearAcked = () => setAcknowledged(new Set());

  return (
    <div className="alerts-page">
      {popup && <AlertPopup alert={popup} onClose={closePopup} onAck={() => ackPopup(popup.id)} />}

      <div className="page-header">
        <h1>🔔 Alerts & Notifications</h1>
        {whatifCount > 0 && (
          <span style={{ fontSize: 12, background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, padding: '3px 10px' }}>
            🧪 {whatifCount} Simulation Active
          </span>
        )}
        {visible.length > 0 && <span className="badge-red">{visible.length} active</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={ackAll}>✅ Ack All</button>
          <button className="btn-secondary" onClick={clearAcked}>↺ Reset</button>
        </div>
      </div>

      {/* Alert email config */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 18px' }}>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 10, fontWeight: 600 }}>📧 Send alerts to</div>
        {/* Existing emails */}
        {alertEmails.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {alertEmails.map(email => (
              <div key={email} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 20,
                background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)',
              }}>
                <span style={{ fontSize: 12, color: '#a78bfa' }}>{email}</span>
                <button onClick={() => removeEmail(email)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#6b7280', fontSize: 13, lineHeight: 1, padding: 0,
                }}>✕</button>
              </div>
            ))}
          </div>
        )}
        {/* Add new email */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="email"
            placeholder="Add email address..."
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addEmail()}
            style={{
              flex: 1, padding: '7px 12px', borderRadius: 8,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
              color: '#e5e7eb', fontSize: 13, outline: 'none',
            }}
          />
          <button onClick={addEmail} style={{
            padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700,
            background: emailSaved ? '#34d399' : '#a78bfa', color: '#0a0e1a', transition: 'background 0.3s',
          }}>
            {emailSaved ? '✅ Saved!' : '+ Add'}
          </button>
        </div>
        {alertEmails.length === 0 && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>No emails added — alerts will not be sent</div>
        )}
      </div>

      {/* Cross-machine correlation banner */}
      {correlation && (() => {
        const typeColor = correlation.type === 'ENVIRONMENTAL' ? '#f87171'
          : correlation.type === 'CASCADING' ? '#f97316' : '#60a5fa';
        const typeIcon  = correlation.type === 'ENVIRONMENTAL' ? '🌐'
          : correlation.type === 'CASCADING' ? '🔗' : '🔧';
        const typeLabel = correlation.type === 'ENVIRONMENTAL' ? 'Environmental / Systemic'
          : correlation.type === 'CASCADING' ? 'Cascading Failure' : 'Machine-Specific';
        return (
          <div style={{
            marginBottom: 20, borderRadius: 10, overflow: 'hidden',
            border: `1px solid ${typeColor}55`,
          }}>
            <div style={{
              padding: '10px 16px', background: `${typeColor}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{typeIcon}</span>
                <div>
                  <span style={{ fontWeight: 700, color: typeColor, fontSize: 14 }}>
                    {typeLabel} Fault Detected
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 10 }}>
                    {correlation.confidence}% confidence
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {correlation.affectedMachines.map(m => (
                  <span key={m} style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    background: `${typeColor}22`, color: typeColor, border: `1px solid ${typeColor}44`,
                  }}>{m}</span>
                ))}
              </div>
            </div>
            <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.25)', fontSize: 13 }}>
              <div style={{ color: '#e5e7eb', marginBottom: 6 }}>{correlation.insight}</div>
              <div style={{ color: '#34d399', fontSize: 12 }}>🔧 {correlation.action}</div>
              {correlation.sharedPatterns.length > 0 && (
                <div style={{ color: '#fbbf24', fontSize: 12, marginTop: 4 }}>
                  Shared pattern: {correlation.sharedPatterns.join(', ')}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {alerts.length === 0 && (
        <div className="card" style={{ padding: 24, marginBottom: 20, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>🧪</span>
            <div>
              <div style={{ fontWeight: 600, color: '#e5e7eb', marginBottom: 4 }}>No active simulation</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                Go to <strong style={{ color: '#a78bfa' }}>ML Insights → What-If Simulator</strong>, pick a failure scenario and press Start to generate alerts here.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="alert-summary">
        {(['ALL', 'Critical', 'Medium', 'Low'] as FilterType[]).map(s => (
          <button key={s}
            className={`summary-btn ${filter === s ? 'active' : ''} sev-${s.toLowerCase()}`}
            onClick={() => setFilter(s)}>
            {s === 'ALL'
              ? `All (${alerts.length})`
              : `${SEVERITY_ICON[s as keyof typeof SEVERITY_ICON]} ${s} (${counts[s as keyof typeof counts]})`}
          </button>
        ))}
      </div>

      <div className="alert-feed card">
        {visible.length === 0 ? (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🧪</div>
            <p style={{ fontWeight: 600, marginBottom: 6 }}>No simulation alerts</p>
            <p style={{ fontSize: 12 }}>Open the What-If Simulator in ML Insights and run a scenario</p>
          </div>
        ) : (
          visible.map(a => {
            const color = SEVERITY_COLOR[a.severity];
            const ctDelta = a.cycletime && a.ctBaseline
              ? (((a.cycletime - a.ctBaseline) / a.ctBaseline) * 100).toFixed(1)
              : null;
            return (
              <div key={a.id}
                className={`alert-row sev-${a.severity.toLowerCase()}`}
                style={{ borderLeft: `4px solid ${color}`, cursor: 'pointer' }}
                onClick={() => setPopup(a)}>
                <div className="alert-icon">{SEVERITY_ICON[a.severity]}</div>
                <div className="alert-body">
                  <div className="alert-title">
                    <strong>{a.machineName}</strong>
                    {a.healthScore !== undefined && (
                      <span style={{ fontSize: 11, marginLeft: 8, color:
                        a.healthScore > 70 ? '#34d399' : a.healthScore > 40 ? '#fbbf24' : '#f87171' }}>
                        ❤️ {a.healthScore}%
                      </span>
                    )}
                    {a.confidence !== undefined && (
                      <span style={{ fontSize: 11, marginLeft: 6, color: '#a78bfa' }}>
                        {a.confidence}% conf
                      </span>
                    )}
                    {' '}— {a.sensorName}
                    {a.stage && (
                      <span style={{ fontSize: 10, marginLeft: 8, padding: '1px 7px', borderRadius: 4,
                        background: `${color}22`, color, border: `1px solid ${color}55` }}>
                        {a.stage}
                      </span>
                    )}
                    <span style={{ fontSize: 10, marginLeft: 6, padding: '1px 6px', borderRadius: 4,
                      background: a.source === 'ewma' ? 'rgba(52,211,153,0.15)' : a.source === ('slider' as any) ? 'rgba(251,191,36,0.15)' : 'rgba(167,139,250,0.15)',
                      color: a.source === 'ewma' ? '#34d399' : a.source === ('slider' as any) ? '#fbbf24' : '#a78bfa' }}>
                      {a.source === 'ewma' ? '📡 Live' : a.source === ('slider' as any) ? '🎮 Slider' : '🧪 Sim'}
                    </span>
                    {a.recommendedAction.includes('Recovering') && (
                      <span style={{ fontSize: 10, marginLeft: 6, padding: '1px 6px', borderRadius: 4,
                        background: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}>
                        🔄 Recovery
                      </span>
                    )}
                  </div>
                  <div className="alert-meta">
                    {a.timestamp}
                    {a.ewmaTrend !== undefined && <> · EWMA: <span style={{ color }}>{a.ewmaTrend.toFixed(2)}{a.unit}</span></>}
                    {a.slope !== undefined && <> · Slope: <span style={{ color: a.slope > 0.01 ? '#f87171' : '#fbbf24' }}>{a.slope > 0 ? '+' : ''}{a.slope.toFixed(4)}</span></>}
                    {ctDelta && <> · CT: <span style={{ color: '#f97316' }}>+{ctDelta}%</span></>}
                    {a.ttf && a.ttf > 0 && <> · TTF: <span style={{ color: a.ttf < 50 ? '#f87171' : '#fbbf24' }}>~{a.ttf} cycles</span></>}
                  </div>
                  <div style={{ fontSize: 12, color: '#a78bfa', marginTop: 4 }}>💡 {a.recommendedAction}</div>
                  {a.detectedPatterns && a.detectedPatterns.length > 0 && (
                    <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 3 }}>
                      {a.detectedPatterns[0].icon} {a.detectedPatterns[0].name}
                      <span style={{ color: '#6b7280', marginLeft: 6 }}>{a.detectedPatterns[0].matchScore}% match</span>
                    </div>
                  )}
                  {a.baselineShiftSensors && a.baselineShiftSensors.length > 0 && (
                    <div style={{ fontSize: 11, color: '#f97316', marginTop: 3 }}>
                      📈 Baseline shifted: {a.baselineShiftSensors.map(s => s.name).join(', ')}
                    </div>
                  )}
                </div>
                <button className="ack-btn" onClick={e => { e.stopPropagation(); ack(a.id); }}>✓ Ack</button>
              </div>
            );
          })
        )}
      </div>

      {alerts.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ marginBottom: 16 }}>📊 Alert Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
            {(['Critical', 'High', 'Medium', 'Low'] as const).map(s => (
              <div key={s} style={{ padding: 12, background: `${SEVERITY_COLOR[s]}18`, borderRadius: 8, borderLeft: `3px solid ${SEVERITY_COLOR[s]}` }}>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>{s}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: SEVERITY_COLOR[s] }}>{counts[s]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
